
import React from 'react'
import { Link, useParams, useSearchParams } from "react-router-dom";
import { EditOutlined } from "@mui/icons-material";
import { Button, Divider } from "@mui/material";
import { toast } from "react-toastify";

import { CustomerDetailContext, useCustomerDetailContext } from "contexts/CustomerDetailContext";

import { useGetCustomerDetailQuery, useLazyListCustomerShipmentsQuery, useUpdateCustomerMutation } from "store/services/customerApi";

import EnableDisableButton from "components/Shared/EnableDisableButton";
import CustomerBasicDetail from "./customerComponents/CustomerBasicDetail";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useAppMeta, useAuth } from "hooks";
import CustomTabs from "components/Shared/CustomTabs";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import CustomTabPanel from "components/Shared/CustomTabPanel";
import CustomerNotesTab from "./customerComponents/CustomerNotesTab";
import CustomerShipmentTab from "./customerComponents/CustomerShipmentTab";


const CustomerDetailScreen = () => {

    const { id } = useParams();

    const [totalShipment, setTotalShipments] = React.useState(0);

    const [searchParams] = useSearchParams();

    const activeTab = searchParams.get("tab") || "notes";

    const breadcrumbs = [
        { title: "Customer Profile", path: "/app/crm/customerProfile/list" },
        { title: "Customer Profile Detail" },
    ];

    // RTK Query
    const [fetchesShipments, { data: shipmentRes }] = useLazyListCustomerShipmentsQuery();

    React.useEffect(() => {
        if (shipmentRes?.totalItems !== undefined) {
            setTotalShipments(shipmentRes.totalItems);
        }
    }, [shipmentRes?.totalItems]);

    // RTK Query
    const { data: customerDetail, isLoading: loadingCustomer, error, } = useGetCustomerDetailQuery(id);

    React.useEffect(() => {
        if (customerDetail?._id) {
            // Initial fetch for the Shipments tab badge count (before the tab is opened)
            fetchesShipments({ customerId: customerDetail?._id, page: 1, limit: 10 });
        }
    }, [customerDetail?._id, fetchesShipments]);


    //Tabs
    const customerDetailTab = [
        { label: "Notes", value: "notes", badgeCount: customerDetail?.notes?.length },

        { label: "Shipments", value: "shipments", badgeCount: totalShipment }
    ]

    useAppMeta("Customer Detail");

    const PageActions = () => {

        const { isAccountAdmin } = useAuth();

        const { customerDetail, loadingCustomer } = useCustomerDetailContext();

        // RTK Query
        const [updateCustomer, { isLoading: updatingCustomer }] = useUpdateCustomerMutation();

        // methods
        const handleToggleCustomerStatus = async () => {
            try {
                await updateCustomer({
                    _id: customerDetail._id,
                    isActive: !customerDetail.isActive,
                }).unwrap();
                toast.info(`Customer Successfully ${customerDetail.isActive ? "Disable" : "Enable"} !`);
            } catch (error) {
                toast.error(error?.data?.message ?? "Could not update customer status");
            }
        };
        return (
            <div className="flex space-x-4">
                {isAccountAdmin && (<EnableDisableButton
                    isActive={customerDetail?.isActive}
                    loading={loadingCustomer || updatingCustomer}
                    onClick={handleToggleCustomerStatus}
                />)}

                <Link to={`/app/crm/customerProfile/${customerDetail?._id}/edit`}>
                    <Button startIcon={<EditOutlined />} variant="contained">
                        Edit
                    </Button>
                </Link>
            </div>
        )
    }

    return (
        <CustomerDetailContext.Provider value={{ customerDetail, loadingCustomer }}>
            <BreadcrumbLayout
                breadcrumbs={breadcrumbs}
                isBusy={loadingCustomer}
                headerActions={<PageActions />} >

                <BreadcrumbLayout.Error error={error} />

                <BreadcrumbLayout.Paper>
                    <CustomerBasicDetail />

                    <Divider />

                    <CustomTabs tabs={customerDetailTab} activeTab={activeTab} />

                    {!loadingCustomer ? (
                        <>
                            <CustomTabPanel activeTab={activeTab} value="shipments">
                                <CustomerShipmentTab setTotalShipments={setTotalShipments} />
                            </CustomTabPanel>

                            <CustomTabPanel activeTab={activeTab} value="notes">
                                <CustomerNotesTab />
                            </CustomTabPanel>

                        </>
                    ) : (
                        <RowsSkeleton />
                    )}

                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>

        </CustomerDetailContext.Provider>
    );
}

export default CustomerDetailScreen;


